import * as React from "react";
import * as moment from "moment";

import { useSelector } from "react-redux";

import { Container, Grid, Tab, TabProps, Loader } from "semantic-ui-react";

import { LinkObject } from "@teamprevent/common/model";
import { goToParent } from "@teamprevent/common/routing";

import { useDispatch } from "@teamprevent/admin-redux";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { OldCaseConversationTab } from "@teamprevent/common/messaging/components/OldCaseConversationTab";

import { loadClinics } from "../../../calendar/clinics/actions";

import { basePathSelector, routeIdSelector as ohCaseIdSelector } from "../selectors";
import { updateAssociatedManagers, updateAssociatedHrUsers, loadOhCaseDetail } from "../actions";
import { ApplicationState } from "../../../applicationState";
import { AddTaskModal } from "../../../tasks/components/AddTaskModal";

import { useOhCaseDetailsModel } from "../hooks";

import { useCustomerDetailsModel } from "../../customer/hooks";

import * as details from "./details";

import { push, Fragment } from "redux-little-router";

export const Detail: React.FunctionComponent = () => {
    const enableCaseConversationImprovements = getFeatureToggles()[FeatureToggleNames.CaseConversationImprovements];

    const dispatch = useDispatch();
    const ohCaseId = useSelector(ohCaseIdSelector);
    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const basePath = useSelector(basePathSelector);
    const currentTab = useSelector((state: ApplicationState) => state.router.pathname?.substring(
        basePath.length + 1 + ohCaseIdSelector(state).toString().length));

    const { ohCase, isLoaded: isOhCaseLoaded } = useOhCaseDetailsModel({ ohCaseId, customerId });
    const { customer, isLoaded: isCustomerLoaded } = useCustomerDetailsModel({ customerId });

    React.useEffect(() => {
        dispatch(loadClinics(
            false,
            null,
            null,
            moment().startOf("day"),
            [],
            [],
            null,
            "day",
            null));
    }, [basePath, customerId, dispatch, isCustomerLoaded, isOhCaseLoaded, ohCaseId]);

    const refreshCase = React.useCallback(() =>
        dispatch(loadOhCaseDetail({ ohCaseId, customerId, forceLoad: true }))
    , [customerId, dispatch, ohCaseId]);

    const updateAssociatedManagersAction = React.useCallback((_customerId: string, _ohCaseId: string, managers: LinkObject[]) =>
        dispatch(updateAssociatedManagers(customerId, ohCaseId, managers)), [customerId, dispatch, ohCaseId]);

    const updateAssociatedHrUsersAction = React.useCallback((_customerId: string, _ohCaseId: string, managers: LinkObject[]) =>
        dispatch(updateAssociatedHrUsers(customerId, ohCaseId, managers)), [customerId, dispatch, ohCaseId]);

    const panes = React.useMemo(() => {
        let list: (ReturnType<typeof details[keyof typeof details]>)[] = [];

        if (isOhCaseLoaded && isCustomerLoaded) {
            const documentsTab = details.documentsTab(customerId, ohCaseId);
            const conversationTab = enableCaseConversationImprovements ? details.caseConversationTab(ohCaseId) : OldCaseConversationTab();

            list = [details.coreDetailsTab({
                ohCase,
                customerId,
                customer,
                refreshCase,
                updateAssociatedManagers: updateAssociatedManagersAction,
                updateAssociatedHrUsers: updateAssociatedHrUsersAction
            }), documentsTab, conversationTab];

            if (customer.purchaseOrderRequiredPerCase) {
                list.push(details.FinanceInformationTab());
            }
        }

        return list;
    }, [customer, customerId, enableCaseConversationImprovements, isCustomerLoaded, isOhCaseLoaded, ohCase, ohCaseId,
        refreshCase, updateAssociatedHrUsersAction, updateAssociatedManagersAction]);

    const handleTabChange = React.useCallback((event: any, { activeIndex }: TabProps) => {
        if (ohCase) {
            dispatch(push(`${basePath}/${ohCase.id}${panes[activeIndex as unknown as number].path}`));
        }
    }, [basePath, dispatch, ohCase, panes]);

    const goToParentRoute = React.useCallback(() => dispatch(goToParent()), [dispatch]);

    const currentIndex = React.useMemo(() => {
        return panes.slice(1).findIndex(p => currentTab && currentTab.startsWith(p.path)) + 1;
    }, [currentTab, panes]);

    if (!isOhCaseLoaded || !isCustomerLoaded) {
        return <Loader active />;
    }

    return (
        <Container>
            <h1>Case {ohCase.caseId}</h1>
            <p>Created {ohCase.created && ohCase.created.format("LL")}</p>
            <p>Last updated {ohCase.lastUpdated && ohCase.lastUpdated.format("LL")}</p>
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column>
                        <Tab panes={panes} onTabChange={handleTabChange} activeIndex={currentIndex} />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Fragment forRoute="firstTask">
                <AddTaskModal customerId={customerId} ohCase={ohCase} open onClose={goToParentRoute} />
            </Fragment>
        </Container>
    );
};
